/*
  SoapSessionDialog — the therapist's control panel for one assessment.

  Two modes, driven by the RecordingProvider state:
    1. Pre-record → pick appointment type + optional SbN client, tap
       "Start recording". The pill in the corner shows live status.
    2. Post-record → transcript + generated SOAP note appear inline;
       therapist edits any section and taps Save.
*/
import { useEffect, useMemo, useState } from "react";
import { SendResourceButton } from "@/components/resources/SendResourceDialog";
import { useCanonicalClient } from "@/lib/canonicalClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useRecording } from "@/lib/recording";
import SoapRecoveryBanner from "@/components/SoapRecoveryBanner";
import LiveUploadStatus from "@/components/LiveUploadStatus";
import MicPicker from "@/components/MicPicker";
import FollowUpFields, { isEpFollowUp } from "@/components/FollowUpFields";
import NoteStatusControl from "@/components/NoteStatusControl";
import DeleteWithReasonDialog from "@/components/DeleteWithReasonDialog";
import { DialogExpandToggle, EXPANDED_DIALOG_CLASSES } from "@/components/DialogExpandToggle";
import { useUiPref } from "@/lib/uiPrefs";
import useStickyState from "@/hooks/useStickyState";
import { useAuth } from "@/lib/auth";
import {
  Mic, Loader2, CheckCircle2, AlertTriangle, Sparkles, Save, X, Search,
  StickyNote, FileDown, Pencil, History, User as UserIcon, Plus, Trash2,
  ShieldAlert, ShieldCheck,
} from "lucide-react";

const APPT_TYPES = [
  "Initial Assessment",
  "Follow-up Assessment",
  "Exercise Physiology Consult",
  "Exercise Physiology – Follow-Up Consultation",
  "Pilates 1:1",
  "Pilates Reformer",
  "ONERO® session",
  "Other",
];

function SbNPicker({ query, chosen, onChoose, onClear }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(query || "");
  const [notConfigured, setNotConfigured] = useState(false);

  async function search() {
    if (!q || q.length < 2) return;
    setLoading(true);
    try {
      const r = await api.get("/soap-notes/sbn/clients", { params: { q } });
      if (!r.data.configured) { setNotConfigured(true); setResults([]); return; }
      setResults(r.data.clients || []);
    } catch (e) {
      toast.error("SbN lookup failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  if (chosen) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs">
        <Sparkles className="h-3.5 w-3.5 text-emerald-700" />
        <span className="text-emerald-800">
          SbN linked: <strong>{chosen.first_name} {chosen.last_name}</strong>
          {chosen.sessionCount ? ` · ${chosen.sessionCount} sessions` : ""}
        </span>
        <button
          type="button"
          onClick={onClear}
          className="ml-1 text-emerald-800 hover:text-emerald-900"
          data-testid="sbn-clear"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search Strength by Numbers…"
          className="h-8 text-xs"
          data-testid="sbn-search-input"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
        />
        <Button
          type="button" size="sm" variant="outline"
          onClick={search} disabled={loading || q.length < 2}
          data-testid="sbn-search-btn"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {notConfigured && (
        <div className="text-2xs text-zinc-500">SbN API key not configured on backend.</div>
      )}
      {results.length > 0 && (
        <ul className="border border-zinc-200 rounded-md divide-y max-h-40 overflow-y-auto">
          {results.map((c) => (
            <li
              key={c.client_id}
              className="px-2 py-1.5 text-xs hover:bg-zinc-50 cursor-pointer flex justify-between"
              onClick={() => onChoose(c)}
              data-testid={`sbn-result-${c.client_id}`}
            >
              <span>
                <strong>{c.first_name} {c.last_name}</strong>
                {c.email ? <span className="text-zinc-500"> · {c.email}</span> : null}
              </span>
              <span className="text-zinc-400">{c.sessionCount || 0} sess</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PainScoreRow({ row, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-[1fr_90px_1fr_28px] gap-2 items-center">
      <Input
        value={row.location || ""}
        onChange={(e) => onChange({ ...row, location: e.target.value })}
        placeholder="Location (e.g. R knee)" className="h-8 text-xs"
      />
      <Input
        type="number" min={0} max={10} step={0.5}
        value={row.score_0_10 ?? ""}
        onChange={(e) => onChange({ ...row, score_0_10: e.target.value === "" ? null : Number(e.target.value) })}
        placeholder="0-10" className="h-8 text-xs font-mono-num"
      />
      <Input
        value={row.context || ""}
        onChange={(e) => onChange({ ...row, context: e.target.value })}
        placeholder="Context (rest / with movement)" className="h-8 text-xs"
      />
      <button
        type="button" onClick={onRemove}
        className="text-zinc-400 hover:text-red-600"
      ><X className="h-4 w-4" /></button>
    </div>
  );
}

function RomRow({ row, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_100px_80px_28px] gap-2 items-center">
      <Input value={row.joint || ""} onChange={(e) => onChange({ ...row, joint: e.target.value })} placeholder="Joint" className="h-8 text-xs" />
      <Input value={row.movement || ""} onChange={(e) => onChange({ ...row, movement: e.target.value })} placeholder="Movement" className="h-8 text-xs" />
      <Input value={row.value || ""} onChange={(e) => onChange({ ...row, value: e.target.value })} placeholder="Value" className="h-8 text-xs" />
      <Input value={row.side || ""} onChange={(e) => onChange({ ...row, side: e.target.value })} placeholder="Side" className="h-8 text-xs" />
      <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-red-600"><X className="h-4 w-4" /></button>
    </div>
  );
}

function ExerciseRow({ row, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-[1.6fr_70px_70px_70px_1fr_28px] gap-2 items-center">
      <Input value={row.name || ""} onChange={(e) => onChange({ ...row, name: e.target.value })} placeholder="Exercise" className="h-8 text-xs" />
      <Input value={row.sets || ""} onChange={(e) => onChange({ ...row, sets: e.target.value })} placeholder="Sets" className="h-8 text-xs" />
      <Input value={row.reps || ""} onChange={(e) => onChange({ ...row, reps: e.target.value })} placeholder="Reps" className="h-8 text-xs" />
      <Input value={row.load || ""} onChange={(e) => onChange({ ...row, load: e.target.value })} placeholder="Load" className="h-8 text-xs" />
      <Input value={row.notes || ""} onChange={(e) => onChange({ ...row, notes: e.target.value })} placeholder="Notes" className="h-8 text-xs" />
      <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-red-600"><X className="h-4 w-4" /></button>
    </div>
  );
}

export default function SoapSessionDialog({ open, onClose, clientEmail, clientName, onSaved, initiatingAssessmentId }) {
  const [expanded, setExpanded] = useUiPref("soap_recorder_expanded", false);
  const rec = useRecording();
  const canonical = useCanonicalClient();
  const [appointmentType, setAppointmentType] = useState(APPT_TYPES[0]);
  const [therapistNotes, setTherapistNotes] = useState("");
  const [sbnClient, setSbnClient] = useState(null);
  const [note, setNote] = useState(null);     // once generated, holds SOAP note doc
  const [serverNoteSnapshot, setServerNoteSnapshot] = useState(null);  // last-server-known state (for amendment diff)

  // Whenever we get a fresh doc from the server, update both live-note
  // and the diff baseline so amendments know what actually changed.
  const applyServerNote = (doc) => {
    setNote(doc);
    setServerNoteSnapshot(doc ? { ...doc } : null);
  };
  const [transcript, setTranscript] = useState("");
  const [saving, setSaving] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  // Structured EP Follow-Up data. Only sent to backend when the
  // appointment type / template flags it as a follow-up.
  const [followUp, setFollowUp] = useState({});

  const isFollowUp = useMemo(
    () => isEpFollowUp({
      appointmentType,
      template: templates.find((t) => t.id === templateId),
    }),
    [appointmentType, templateId, templates],
  );

  // Load templates when the dialog opens
  useEffect(() => {
    if (!open) return;
    api.get("/soap-notes/templates")
      .then((r) => setTemplates(r.data || []))
      .catch(() => { /* non-blocking */ });
  }, [open]);

  // When the therapist picks a template, pre-fill appointment type + context
  function applyTemplate(id) {
    setTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    if (t.default_appointment_type) setAppointmentType(t.default_appointment_type);
    if (t.default_extra_context) setTherapistNotes(t.default_extra_context);
  }

  const isForThisClient = useMemo(() => {
    return rec.session && rec.session.client_email === (clientEmail || "").toLowerCase();
  }, [rec.session, clientEmail]);

  const canStart = !rec.isActive || !isForThisClient;

  // Consent gate — hard block. Fetches the client's recording consent
  // status when the dialog opens and disables Start until an active
  // consent is on file. Backend enforces the same gate (returns
  // recording_consent_missing), so this is a UX safety net that
  // stops the practitioner attempting to record at all.
  const [consentActive, setConsentActive] = useState(null);   // null = loading
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentAttest, setConsentAttest] = useState(false);
  useEffect(() => {
    if (!open || !clientEmail) return;
    let cancelled = false;
    setConsentActive(null);
    (async () => {
      try {
        const r = await api.get(
          `/clients/${encodeURIComponent(clientEmail)}/consents/status?type=recording`,
        );
        if (!cancelled) setConsentActive(!!r.data?.active);
      } catch {
        if (!cancelled) setConsentActive(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientEmail]);

  async function captureConsentNow() {
    if (!consentAttest) {
      toast.error("Please tick the attestation checkbox first");
      return;
    }
    setConsentBusy(true);
    try {
      await api.post(`/clients/${encodeURIComponent(clientEmail)}/consents`, {
        consent_type: "recording",
        source: "verbal",
        signed_by_client: false,
        notes: "Verbal consent captured at the start of the consultation. "
                + "Practitioner attested to reading the recording policy to the client.",
      });
      setConsentActive(true);
      toast.success("Recording consent captured");
    } catch (e) {
      toast.error("Couldn't record consent", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setConsentBusy(false); }
  }

  useEffect(() => {
    if (!open) return;
    // If a recording just finished for THIS client, show the generated
    // note. Guard on client_email — otherwise a "done" session left in
    // rec state from a previous client would hijack this dialog and
    // unmount the recovery banner before the user could tap Retry.
    async function loadIfDone() {
      if (rec.status !== "done" || !rec.session?.id) return;
      const sessionEmail = (rec.session.client_email || "").toLowerCase();
      const openFor = (clientEmail || "").toLowerCase();
      if (sessionEmail && openFor && sessionEmail !== openFor) return;
      try {
        const r = await api.get(`/soap-notes/sessions/${rec.session.id}`);
        if (r.data.note) { applyServerNote(r.data.note); setTranscript(r.data.note.transcript || ""); }
      } catch { /* ignore */ }
    }
    loadIfDone();
  }, [open, rec.status, rec.session, clientEmail]);

  async function start() {
    if (!clientEmail) return toast.error("Client email missing");
    try {
      await rec.startRecording({
        client_email: clientEmail,
        client_name: clientName,
        appointment_type: appointmentType,
        sbn_client_id: sbnClient?.client_id || null,
        notes: therapistNotes,
        template_id: templateId || null,
        follow_up: isFollowUp ? followUp : null,
        // Origin marker — recording started from an Assessment
        // Editor sends the assessment id so the finalise pipeline
        // (including recovery-vault retries) can auto-link the
        // resulting SOAP session back to the assessment.
        initiating_assessment_id: initiatingAssessmentId || null,
        // Phase-2 scoped writes — when the dialog is opened from a
        // `?as=<cid>` workspace the new SOAP session (and downstream
        // note) get stamped so they show up on Judy's canonical page
        // and NOT Rosemarie's.
        canonical_client_id: canonical?.clientId || null,
      });
      toast.success("Recording started", { description: "You can navigate anywhere — the recording keeps running." });
    } catch (e) {
      // Server-side consent gate — surface a targeted call-to-action so
      // the practitioner can capture consent without leaving the flow.
      const detail = e?.response?.data?.detail;
      if (detail?.code === "recording_consent_missing") {
        toast.error("Recording consent required", {
          description: `Open the client's Health Record → Consents to capture their audio-recording consent, then start again.`,
          duration: 8000,
        });
        return;
      }
      toast.error("Could not start recording", { description: (typeof detail === "string" ? detail : detail?.message) || e.message });
    }
  }

  // Assessment recordings auto-process on Stop — keep the provider in
  // sync with whatever the practitioner has typed / picked here.
  const { setAutoFinaliseOptions } = rec;
  useEffect(() => {
    if (!open || !initiatingAssessmentId) return;
    setAutoFinaliseOptions({
      assessmentId: initiatingAssessmentId,
      extra_context: therapistNotes,
      include_sbn: !!sbnClient,
      template_id: templateId || null,
      follow_up: isFollowUp ? followUp : null,
    });
  }, [open, initiatingAssessmentId, therapistNotes, sbnClient, templateId, isFollowUp, followUp, setAutoFinaliseOptions]);
  const autoProcess = !!rec.session?.initiating_assessment_id;

  async function finalise() {
    try {
      const result = await rec.finalize({
        extra_context: therapistNotes,
        include_sbn: !!sbnClient,
        template_id: templateId || null,
        follow_up: isFollowUp ? followUp : null,
      });
      applyServerNote(result.note);
      setTranscript(result.note?.transcript || "");
      toast.success("SOAP note generated", { description: "Review, edit and save when ready." });
    } catch (e) {
      toast.error("Finalise failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function saveEdits() {
    if (!note?.id) return;
    // If note is locked (finalised / amended / entered-in-error), the
    // backend rejects direct PATCH — the NoteStatusControl's Amend
    // button captures a reason and calls /amend instead. Guard here so
    // clicking Save on a locked note nudges the user to Amend.
    if (["finalised", "amended", "entered_in_error"].includes(note.status)) {
      toast.info("Note is locked", { description: "Use Amend (with a reason) to change a finalised or amended note." });
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/soap-notes/notes/${note.id}`, {
        subjective: note.subjective,
        objective: note.objective,
        assessment: note.assessment,
        plan: note.plan,
        exercises: note.exercises,
        pain_scores: note.pain_scores,
        range_of_motion: note.range_of_motion,
        therapist_notes: note.therapist_notes || "",
      });
      applyServerNote(r.data);
      toast.success("Note saved");
      onSaved?.(r.data);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  // For NoteStatusControl.getPendingEdits — computes which VERSIONED
  // fields differ from the last-loaded server copy so an amendment
  // payload only carries the actual diff.
  function pendingEdits() {
    if (!note || !serverNoteSnapshot) return {};
    const diff = {};
    for (const f of ["subjective", "objective", "assessment", "plan",
                     "exercises", "pain_scores", "range_of_motion", "therapist_notes"]) {
      if (JSON.stringify(note[f]) !== JSON.stringify(serverNoteSnapshot[f])) {
        diff[f] = note[f];
      }
    }
    return diff;
  }

  function close() {
    // If a recording is still active for this client, don't blow it away —
    // just close the dialog. The floating indicator stays visible.
    setNote(null);
    setTranscript("");
    onClose?.();
  }

  const showEditor = !!note;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        className={`!fixed !inset-0 !top-0 !left-0 !translate-x-0 !translate-y-0 !w-screen !h-[100dvh] !max-w-none !rounded-none !border-0 p-4 gap-3 overflow-y-auto overflow-x-hidden [&>*]:min-w-0 sm:!inset-auto sm:!top-[50%] sm:!left-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:!rounded-lg sm:!border sm:p-6 sm:gap-4 ${expanded ? "sm:!w-[95vw] sm:!max-w-[95vw] sm:!h-[95vh] sm:!max-h-[95vh]" : "sm:!w-full sm:!max-w-3xl sm:!h-auto sm:!max-h-[90vh]"}`}
        data-testid="soap-session-dialog"
      >
        <DialogExpandToggle
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          testId="soap-recorder-expand-toggle"
          className="hidden sm:block"
        />
        <DialogHeader className="pr-14">
          <DialogTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5" /> Assessment session
            {clientName ? <span className="text-zinc-500 font-normal">— {clientName}</span> : null}
          </DialogTitle>
          <DialogDescription>
            Record the session and let the AI draft a SOAP note. You can navigate
            elsewhere while recording — the mic stays live until you tap stop.
          </DialogDescription>
        </DialogHeader>

        {/* Recovery banner sits above the mode-switched body so it's
            never unmounted mid-flow. Even if a stale "done" session
            hijacks `note` and swings us into the editor view, the
            operator can still see and retry interrupted recordings. */}
        <SoapRecoveryBanner
          filterClientEmail={clientEmail}
          onRestored={(res) => {
            if (res?.note) {
              applyServerNote(res.note);
              setTranscript(res.note.transcript || "");
            }
          }}
        />

        {!showEditor && (
          <div className="space-y-4">
            <div className="grid gap-3">
              {templates.length > 0 && (
                <div>
                  <Label className="text-xs">Session template</Label>
                  <select
                    value={templateId}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                    data-testid="soap-template-select"
                    disabled={rec.isActive}
                  >
                    <option value="">— No template (freeform) —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {templateId && (
                    <div className="text-2xs text-zinc-500 mt-1">
                      {templates.find((t) => t.id === templateId)?.description
                        || "Template-driven prompt — AI will follow the saved instructions."}
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs">Session type</Label>
                <select
                  value={appointmentType}
                  onChange={(e) => setAppointmentType(e.target.value)}
                  className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                  data-testid="soap-appt-type"
                  disabled={rec.isActive}
                >
                  {APPT_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Strength by Numbers (optional)</Label>
                <SbNPicker
                  query={clientName || ""}
                  chosen={sbnClient}
                  onChoose={(c) => setSbnClient(c)}
                  onClear={() => setSbnClient(null)}
                />
                <div className="text-2xs text-zinc-500 mt-1">
                  Linking pulls the client&apos;s latest dynamometry / force-plate
                  results into the objective section.
                </div>
              </div>
              <div>
                <Label className="text-xs">Therapist context (optional)</Label>
                <Textarea
                  value={therapistNotes}
                  onChange={(e) => setTherapistNotes(e.target.value)}
                  placeholder="Anything to steer the note — e.g. 'first appointment post-ACL repair, focus on quads'"
                  className="min-h-[70px] text-sm"
                  data-testid="soap-therapist-context"
                  disabled={rec.isActive}
                />
              </div>
            </div>

            {isFollowUp && (
              <FollowUpFields
                clientEmail={clientEmail}
                value={followUp}
                onChange={setFollowUp}
                disabled={rec.isActive}
              />
            )}

            <LiveUploadStatus />

            <MicPicker disabled={rec.isActive} />

            {/* Consent hard-block banner — shows above the recording */}
            {/* controls until an active recording-consent is on file.     */}
            {consentActive === false && rec.status !== "recording" && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3"
                   data-testid="soap-consent-gate">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-900">
                      Recording consent required
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      A recording can't start until this client has an active
                      recording consent on file. Read the recording policy to
                      the client, get their verbal consent, then attest below
                      to capture it. Consent is stored in the client's Health
                      Record with your name and timestamp.
                    </div>
                    <label className="flex items-start gap-2 mt-2 text-xs text-amber-900">
                      <input type="checkbox"
                              checked={consentAttest}
                              onChange={(e) => setConsentAttest(e.target.checked)}
                              className="h-4 w-4 mt-0.5 accent-amber-700"
                              data-testid="soap-consent-attest" />
                      <span>
                        I confirm I have read the recording policy to the
                        client and they have given verbal consent to record
                        this consultation.
                      </span>
                    </label>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="outline"
                                onClick={captureConsentNow}
                                disabled={!consentAttest || consentBusy}
                                data-testid="soap-consent-capture">
                        {consentBusy
                          ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                        Capture verbal consent
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {consentActive === null && rec.status !== "recording" && (
              <div className="text-2xs text-zinc-500"
                   data-testid="soap-consent-loading">
                <Loader2 className="h-3 w-3 inline mr-1 animate-spin" />
                Checking recording consent…
              </div>
            )}

            <div className="rounded-md border border-zinc-200 p-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-xs text-zinc-600">
                  {rec.status === "recording" && (
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" />
                      Recording is live
                    </span>
                  )}
                  {rec.status === "stopped" && (autoProcess
                    ? "Recording stopped — processing automatically…"
                    : "Recording stopped. Tap Finalise to transcribe.")}
                  {rec.status === "finalizing" && "Transcribing audio and drafting SOAP note…"}
                  {rec.status === "idle" && "Ready to record."}
                  {rec.status === "starting" && "Requesting mic access…"}
                  {rec.status === "error" && <span className="text-red-700">{rec.errorMsg || "Recording error"}</span>}
                </div>
                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                  {(rec.status === "idle" || rec.status === "error" || rec.status === "done") && (
                    <Button
                      onClick={start}
                      disabled={!canStart || consentActive !== true}
                      data-testid="soap-start-btn"
                      className="flex-1 sm:flex-none h-11 sm:h-9"
                      title={consentActive === false ? "Recording consent required — capture consent below" : undefined}
                    >
                      <Mic className="h-4 w-4 mr-1" /> Start recording
                    </Button>
                  )}
                  {rec.status === "recording" && (
                    <Button
                      variant="secondary"
                      onClick={rec.stopRecording}
                      data-testid="soap-stop-btn"
                      className="flex-1 sm:flex-none h-11 sm:h-9"
                    >
                      Stop
                    </Button>
                  )}
                  {rec.status === "stopped" && !autoProcess && (
                    <>
                      <Button
                        onClick={finalise}
                        data-testid="soap-finalise-btn"
                        className="flex-1 sm:flex-none h-11 sm:h-9"
                      >
                        <Sparkles className="h-4 w-4 mr-1" /> Finalise &amp; generate SOAP
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          rec.parkSession();
                          toast.success("Consultation parked", {
                            description: "Audio is safely stored. Process the notes later from this client's Notes section.",
                          });
                          close();
                        }}
                        data-testid="soap-park-btn"
                        className="flex-1 sm:flex-none h-11 sm:h-9"
                        title="Save the recording and finish the note later — go straight to your next appointment"
                      >
                        Finish later
                      </Button>
                    </>
                  )}
                  {rec.status === "finalizing" && (
                    <Button disabled className="flex-1 sm:flex-none h-11 sm:h-9">
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Working…
                    </Button>
                  )}
                </div>
              </div>
              {rec.isActive && !isForThisClient && (
                <div className="mt-2 text-xs text-amber-700 flex items-center gap-2 flex-wrap">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {rec.status === "stopped" ? (
                    <>
                      <span className="flex-1">
                        A previous consultation for
                        <strong className="mx-1">{rec.session?.client_name || rec.session?.client_email || "another client"}</strong>
                        is waiting for notes. Park it to record a new one — you can process the notes later from that client&apos;s file.
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          rec.parkSession();
                          toast.success("Previous consultation parked", {
                            description: "You can now start a new recording.",
                          });
                        }}
                        data-testid="soap-park-switch-btn"
                        className="h-8"
                      >
                        Park &amp; start new
                      </Button>
                    </>
                  ) : (
                    <span>Another recording is in progress for a different client. Finalise or discard it first.</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {showEditor && note && (
          <div className="space-y-4">
            {note.warnings?.length ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
                <div className="font-semibold text-amber-900 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Review before signing off
                </div>
                <ul className="mt-1 list-disc pl-5 text-amber-800">
                  {note.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            ) : null}

            <Card className="p-3">
              <SoapField label="Subjective" value={note.subjective} onChange={(v) => setNote({ ...note, subjective: v })} testId="soap-subjective" />
              <SoapField label="Objective" value={note.objective} onChange={(v) => setNote({ ...note, objective: v })} testId="soap-objective" />
              <SoapField label="Assessment" value={note.assessment} onChange={(v) => setNote({ ...note, assessment: v })} testId="soap-assessment" />
              <SoapField label="Plan" value={note.plan} onChange={(v) => setNote({ ...note, plan: v })} testId="soap-plan" />
            </Card>

            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Pain scores</div>
                <Button size="sm" variant="ghost" onClick={() => setNote({ ...note, pain_scores: [...(note.pain_scores || []), { location: "", score_0_10: null, context: "" }] })}>
                  + Add
                </Button>
              </div>
              <div className="space-y-1.5">
                {(note.pain_scores || []).map((row, i) => (
                  <PainScoreRow
                    key={i} row={row}
                    onChange={(v) => {
                      const arr = [...note.pain_scores]; arr[i] = v; setNote({ ...note, pain_scores: arr });
                    }}
                    onRemove={() => setNote({ ...note, pain_scores: note.pain_scores.filter((_, j) => j !== i) })}
                  />
                ))}
                {!(note.pain_scores || []).length && <div className="text-xs text-zinc-400">No pain scores recorded.</div>}
              </div>
            </Card>

            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Range of motion</div>
                <Button size="sm" variant="ghost" onClick={() => setNote({ ...note, range_of_motion: [...(note.range_of_motion || []), { joint: "", movement: "", value: "", side: "" }] })}>
                  + Add
                </Button>
              </div>
              <div className="space-y-1.5">
                {(note.range_of_motion || []).map((row, i) => (
                  <RomRow
                    key={i} row={row}
                    onChange={(v) => { const arr = [...note.range_of_motion]; arr[i] = v; setNote({ ...note, range_of_motion: arr }); }}
                    onRemove={() => setNote({ ...note, range_of_motion: note.range_of_motion.filter((_, j) => j !== i) })}
                  />
                ))}
                {!(note.range_of_motion || []).length && <div className="text-xs text-zinc-400">No ROM captured.</div>}
              </div>
            </Card>

            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Exercises prescribed</div>
                <Button size="sm" variant="ghost" onClick={() => setNote({ ...note, exercises: [...(note.exercises || []), { name: "", sets: "", reps: "", load: "", notes: "" }] })}>
                  + Add
                </Button>
              </div>
              <div className="space-y-1.5">
                {(note.exercises || []).map((row, i) => (
                  <ExerciseRow
                    key={i} row={row}
                    onChange={(v) => { const arr = [...note.exercises]; arr[i] = v; setNote({ ...note, exercises: arr }); }}
                    onRemove={() => setNote({ ...note, exercises: note.exercises.filter((_, j) => j !== i) })}
                  />
                ))}
                {!(note.exercises || []).length && <div className="text-xs text-zinc-400">No exercises prescribed.</div>}
              </div>
            </Card>

            <Card className="p-3 space-y-2">
              <Label className="text-xs">Therapist private notes</Label>
              <Textarea
                value={note.therapist_notes || ""}
                onChange={(e) => setNote({ ...note, therapist_notes: e.target.value })}
                className="min-h-[70px] text-sm"
                placeholder="Optional — not shown in the AI output"
                data-testid="soap-therapist-notes"
              />
              <div className="border-t border-zinc-100 pt-2 mt-1">
                <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">Governance</div>
                <NoteStatusControl
                  note={note}
                  onChange={applyServerNote}
                  getPendingEdits={pendingEdits}
                />
              </div>
            </Card>

            <div>
              <button
                className="text-xs text-zinc-600 underline"
                onClick={() => setShowTranscript((v) => !v)}
              >
                {showTranscript ? "Hide" : "Show"} raw transcript
              </button>
              {showTranscript && (
                <pre className="mt-2 whitespace-pre-wrap text-xs bg-zinc-50 border border-zinc-200 rounded-md p-3 max-h-64 overflow-y-auto">
                  {transcript || "(empty)"}
                </pre>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 sm:space-x-0 sm:flex-wrap">
          <Button
            variant="outline"
            onClick={close}
            data-testid="soap-close-btn"
            className="w-full sm:w-auto h-11 sm:h-9"
          >
            Close
          </Button>
          {showEditor && (
            <Button
              onClick={saveEdits}
              disabled={saving}
              data-testid="soap-save-btn"
              className="w-full sm:w-auto h-11 sm:h-9"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SoapField({ label, value, onChange, testId }) {
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[80px] text-sm"
        data-testid={testId}
      />
    </div>
  );
}

/*
  PendingConsultationsForClient — inline banner shown at the top of the
  Notes section when a consultation was recorded for THIS client but the
  SOAP note hasn't been generated yet. Lets the therapist process the
  notes without leaving the client's record.
*/
function PendingConsultationsForClient({ items, onProcessed, onDiscarded }) {
  const rec = useRecording();
  const [busyId, setBusyId] = useState(null);
  const [processedNote, setProcessedNote] = useState(null);

  async function process(sess) {
    setBusyId(sess.id);
    try {
      const res = await rec.retryFinalize(sess.id, {
        extra_context: sess.therapist_notes || "",
        include_sbn: !!sess.sbn_client_id,
        template_id: sess.template_id || null,
        follow_up: sess.follow_up || null,
      });
      toast.success("SOAP note generated", {
        description: "Review and edit below, then sign off when ready.",
      });
      if (res?.note) {
        setProcessedNote(res.note);
        onProcessed?.(res.note);
      }
    } catch (e) {
      toast.error("Could not process notes", {
        description: e?.message || "The audio may have expired — try again from Recordings vault.",
      });
    } finally {
      setBusyId(null);
    }
  }

  async function discard(sess) {
    if (!window.confirm(
      `Discard the recording for this ${sess.appointment_type || "consultation"}?\n\nThe audio will be permanently deleted and no note will be generated.`,
    )) return;
    setBusyId(sess.id);
    try {
      await rec.discardPendingSession(sess.id);
      toast.success("Recording discarded");
      onDiscarded?.();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div
      className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
      data-testid="pending-consultations-client"
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-amber-900 flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5" />
        {items.length === 1
          ? "Consultation awaiting notes"
          : `${items.length} consultations awaiting notes`}
      </div>
      <p className="text-2xs text-amber-800 mt-1">
        The audio was captured but the SOAP note hasn&apos;t been generated yet.
        Tap <strong>Process notes</strong> to transcribe and draft the note now.
      </p>
      <ul className="mt-2 divide-y divide-amber-200">
        {items.map((s) => {
          const busy = busyId === s.id || s.running;
          return (
            <li
              key={s.id}
              className="py-2 flex items-center gap-3 flex-wrap"
              data-testid={`pending-consultation-${s.id}`}
            >
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-medium text-amber-950">
                  {s.appointment_type || "Consultation"}
                </div>
                <div className="text-2xs text-amber-800 font-mono-num">
                  {s.audio?.segments || 0} segment{s.audio?.segments === 1 ? "" : "s"}
                  {s.started_at ? (
                    <span className="ml-2 opacity-80">
                      · recorded {new Date(s.started_at).toLocaleString()}
                    </span>
                  ) : null}
                  {s.error ? <span className="ml-2 text-red-700">{s.error}</span> : null}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => process(s)}
                  disabled={busy}
                  className="bg-amber-700 hover:bg-amber-800 text-white h-8"
                  data-testid={`pending-consultation-process-${s.id}`}
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 mr-1" />
                      Process notes
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => discard(s)}
                  disabled={busy}
                  className="border-red-300 text-red-700 hover:bg-red-50 h-8"
                  data-testid={`pending-consultation-discard-${s.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Discard
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {processedNote && (
        <div className="text-2xs text-emerald-800 mt-2">
          ✓ Latest note opened below — scroll to review and sign off.
        </div>
      )}
    </div>
  );
}

/*
  Small compact list — shows previously saved SOAP notes for a client.
  Used inside the ClientHistoryDialog on the Intake Forms page.
  Also surfaces any PENDING consultation (audio recorded, note not
  finalised yet) for THIS client with a one-click "Process notes now"
  action so therapists don't have to detour to the Recordings vault.
*/
export function SoapNotesList({ email, refreshKey }) {
  const canonical = useCanonicalClient();
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Status filter — "all" / "draft" / "signed". Persisted per user
  // via localStorage so a clinic that spends most time triaging
  // drafts doesn't have to re-set it on every visit.
  const [statusFilter, setStatusFilter] = useStickyState(
    "all", "soap-notes:status-filter",
  );

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    const targetEmail = email.toLowerCase();
    // Phase-2 scoped filter — when the parent is in `?as=<cid>` mode
    // the list narrows to just that canonical client's notes.
    const notesParams = canonical?.clientId
      ? { canonical_client_id: canonical.clientId } : undefined;
    Promise.all([
      api.get(`/soap-notes/clients/${encodeURIComponent(email)}/notes`,
              notesParams ? { params: notesParams } : undefined)
        .then((r) => r.data || [])
        .catch((e) => {
          toast.error("Could not load SOAP notes", { description: e?.response?.data?.detail || e.message });
          return [];
        }),
      api.get("/soap-notes/sessions/pending")
        .then((r) => (r.data?.sessions || []).filter(
          (s) => (s.client_email || "").toLowerCase() === targetEmail
            && (!canonical?.clientId
                || s.canonical_client_id === canonical.clientId),
        ))
        .catch(() => []),
    ]).then(([notes, pend]) => {
      setRows(notes);
      setPending(pend);
    }).finally(() => setLoading(false));
  }, [email, refreshKey, refreshTick, canonical?.clientId]);

  const bumpRefresh = () => setRefreshTick((n) => n + 1);

  // Apply the status filter client-side — the notes list is
  // paginated at 30/50 rows at most, so filtering after fetch
  // avoids a round-trip and keeps the tab feeling instant.
  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    if (statusFilter === "signed") {
      // "Signed" covers finalised AND amended notes — both are
      // locked and non-editable from the operator's point of view.
      return rows.filter((n) => n.signed_off || ["finalised", "amended"].includes(n.status));
    }
    // draft — anything not-yet-signed. Includes ready_for_review
    // + draft_stale rows since both are still open for edits.
    return rows.filter((n) => !n.signed_off && !["finalised", "amended", "entered_in_error"].includes(n.status));
  }, [rows, statusFilter]);

  if (loading) return <div className="text-xs text-zinc-500 py-2">Loading…</div>;
  if (!rows.length && !pending.length) return null;

  const draftCount = rows.filter((n) => !n.signed_off && !["finalised", "amended", "entered_in_error"].includes(n.status)).length;
  const signedCount = rows.length - draftCount;

  return (
    <section>
      {pending.length > 0 && (
        <PendingConsultationsForClient
          items={pending}
          onProcessed={(note) => {
            setOpen(note);
            bumpRefresh();
          }}
          onDiscarded={bumpRefresh}
        />
      )}
      {rows.length > 0 && (
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <StickyNote className="h-3.5 w-3.5" /> SOAP notes ({filteredRows.length}
            {statusFilter !== "all" && ` / ${rows.length}`})
          </h4>
          <div
            className="inline-flex rounded-md border border-zinc-200 bg-white overflow-hidden text-2xs"
            role="tablist"
            aria-label="Filter SOAP notes by status"
            data-testid="soap-notes-status-filter"
          >
            {[
              { v: "all", label: `All (${rows.length})` },
              { v: "draft", label: `Draft (${draftCount})` },
              { v: "signed", label: `Signed (${signedCount})` },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                role="tab"
                aria-selected={statusFilter === opt.v}
                onClick={() => setStatusFilter(opt.v)}
                className={`px-2.5 py-1 uppercase tracking-wider transition-colors ${
                  statusFilter === opt.v
                    ? "bg-zinc-950 text-white"
                    : "text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid={`soap-notes-filter-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <ul className="space-y-2">
        {filteredRows.length === 0 ? (
          <li className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-3"
              data-testid="soap-notes-empty-for-filter">
            No {statusFilter === "draft" ? "draft" : "signed"} notes for this client yet.
          </li>
        ) : null}
        {filteredRows.map((n) => (
          <li
            key={n.id}
            className="rounded-lg border border-zinc-200 px-4 py-2 hover:bg-zinc-50 cursor-pointer"
            onClick={() => setOpen(n)}
            data-testid={`soap-note-item-${n.id}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              {n.signed_off ? (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-2xs uppercase tracking-wider border">
                  Signed
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-2xs uppercase tracking-wider border">
                  Draft
                </Badge>
              )}
              {n.draft_stale && !n.signed_off && (
                <Badge
                  className="bg-rose-100 text-rose-800 border-rose-300 text-2xs uppercase tracking-wider border"
                  title="Transcript-based facts were accepted into the assessment after this draft was generated — review before finalising."
                  data-testid={`soap-note-stale-badge-${n.id}`}
                >
                  needs review
                </Badge>
              )}
              {n.generated_by === "emergent_fallback" && (
                <Badge
                  className="bg-orange-100 text-orange-800 border-orange-200 text-2xs uppercase tracking-wider border"
                  title={
                    "Generated via Claude fallback because the OpenRouter call failed. "
                    + (n.fallback_reason || "See email for details.")
                  }
                  data-testid={`soap-note-fallback-badge-${n.id}`}
                >
                  fallback
                </Badge>
              )}
              <span className="text-sm font-medium text-zinc-900">
                {n.appointment_type || "Assessment"}
              </span>
              <span className="text-2xs text-zinc-500 font-mono-num">
                {new Date(n.created_at).toLocaleString()}
              </span>
            </div>
            {n.assessment && (
              <div className="mt-1 text-xs text-zinc-600 line-clamp-2">
                {n.assessment}
              </div>
            )}
          </li>
        ))}
      </ul>
      {open && (
        <SoapNoteReadDialog note={open} onClose={() => setOpen(null)} />
      )}
    </section>
  );
}

function SoapNoteReadDialog({ note: initialNote, onClose }) {
  const [expanded, setExpanded] = useUiPref("soap_note_viewer_expanded", false);
  const { can } = useAuth();
  const isAdmin = can("owner", "manager");
  const [note, setNote] = useState(initialNote);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  // Buffer during edit so cancel truly discards.
  const [draft, setDraft] = useState({
    subjective: initialNote.subjective || "",
    objective: initialNote.objective || "",
    assessment: initialNote.assessment || "",
    plan: initialNote.plan || "",
    therapist_notes: initialNote.therapist_notes || "",
    exercises: Array.isArray(initialNote.exercises) ? initialNote.exercises : [],
    pain_scores: Array.isArray(initialNote.pain_scores) ? initialNote.pain_scores : [],
    range_of_motion: Array.isArray(initialNote.range_of_motion) ? initialNote.range_of_motion : [],
  });

  useEffect(() => { setNote(initialNote); }, [initialNote]);

  function startEdit() {
    setDraft({
      subjective: note.subjective || "",
      objective: note.objective || "",
      assessment: note.assessment || "",
      plan: note.plan || "",
      therapist_notes: note.therapist_notes || "",
      exercises: Array.isArray(note.exercises)
        ? note.exercises.map((r) => ({ ...r })) : [],
      pain_scores: Array.isArray(note.pain_scores)
        ? note.pain_scores.map((r) => ({ ...r })) : [],
      range_of_motion: Array.isArray(note.range_of_motion)
        ? note.range_of_motion.map((r) => ({ ...r })) : [],
    });
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  // Sub-list mutators — shared shape (key === draft key).
  function updateRow(key, idx, patch) {
    setDraft((d) => ({
      ...d,
      [key]: d[key].map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));
  }
  function addRow(key, blank) {
    setDraft((d) => ({ ...d, [key]: [...d[key], { ...blank }] }));
  }
  function removeRow(key, idx) {
    setDraft((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) }));
  }

  async function save() {
    setSaving(true);
    try {
      // Strip empty rows so the audit trail doesn't fill up with blank
      // placeholders left over from Add-row clicks the user didn't fill.
      const cleanedExercises = draft.exercises.filter(
        (r) => (r.name || "").trim() || r.sets || r.reps || (r.load || "").trim() || (r.notes || "").trim(),
      );
      const cleanedPain = draft.pain_scores.filter(
        (r) => (r.location || "").trim() || r.score_0_10 != null,
      );
      const cleanedRom = draft.range_of_motion.filter(
        (r) => (r.joint || "").trim() || (r.movement || "").trim() || (r.value || "").trim(),
      );
      const r = await api.patch(`/soap-notes/notes/${note.id}`, {
        subjective: draft.subjective,
        objective: draft.objective,
        assessment: draft.assessment,
        plan: draft.plan,
        therapist_notes: draft.therapist_notes,
        exercises: cleanedExercises,
        pain_scores: cleanedPain,
        range_of_motion: cleanedRom,
      });
      setNote(r.data);
      setEditing(false);
      toast.success(`Note saved (v${r.data.version || 1})`);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  const version = note.version || 1;
  const editedLine = note.last_edited_at ? (
    <span className="inline-flex items-center gap-1 text-2xs text-zinc-500">
      <UserIcon className="h-3 w-3" /> {note.last_edited_by_name || "Unknown"} · {new Date(note.last_edited_at).toLocaleString()}
    </span>
  ) : null;

  return (
    <Dialog open={!!note} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className={`overflow-y-auto overflow-x-hidden [&>*]:min-w-0 ${expanded ? EXPANDED_DIALOG_CLASSES : "max-w-2xl max-h-[90vh]"}`}
        data-testid="soap-note-read-dialog"
        data-expanded={expanded ? "true" : "false"}
      >
        <DialogExpandToggle
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          testId="soap-note-expand-toggle"
        />
        <DialogHeader className="pr-14">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {note.appointment_type || "Assessment"} — {new Date(note.created_at).toLocaleString()}
            <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 text-2xs uppercase tracking-wider border" data-testid="soap-note-version-badge">
              v{version}
            </Badge>
            {version > 1 && (
              <button
                onClick={() => setShowHistory(true)}
                className="text-2xs underline text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"
                data-testid="soap-note-history-btn"
              >
                <History className="h-3 w-3" /> View history ({version - 1} edit{version - 1 === 1 ? "" : "s"})
              </button>
            )}
          </DialogTitle>
          {editedLine && (
            <div className="text-2xs text-zinc-500">Last edited by {editedLine}</div>
          )}
          {note.generated_by === "emergent_fallback" && (
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900"
              data-testid="soap-note-fallback-notice"
            >
              <Badge className="bg-orange-200 text-orange-900 border-orange-300 text-2xs uppercase tracking-wider border shrink-0">
                fallback
              </Badge>
              <div>
                <div className="font-semibold">Generated via Emergent Claude fallback.</div>
                <div className="text-2xs text-orange-800/90 mt-0.5">
                  OpenRouter call failed{note.fallback_reason ? ` — ${note.fallback_reason}` : "."}
                  {" "}Check <b>Settings → OpenRouter</b>.
                </div>
              </div>
            </div>
          )}
          {note.draft_stale && !note.signed_off && (
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900"
              data-testid="soap-note-stale-banner"
            >
              <Badge className="bg-rose-200 text-rose-900 border-rose-300 text-2xs uppercase tracking-wider border shrink-0">
                needs review
              </Badge>
              <div className="flex-1">
                <div className="font-semibold">Draft may be out of date.</div>
                <div className="text-2xs text-rose-800/90 mt-0.5">
                  {(note.stale_events || []).length > 0 ? (
                    <>
                      Transcript-based facts have been accepted into the assessment
                      for{" "}
                      <b>
                        {(note.stale_events || [])
                          .slice(-3)
                          .map((e) => e.field_label || e.field_key)
                          .join(", ")}
                      </b>
                      {(note.stale_events || []).length > 3 && (
                        <> and {(note.stale_events || []).length - 3} more</>
                      )}
                      {" "}since this draft was generated.
                    </>
                  ) : (
                    "Accepted transcript facts have changed since this draft was generated."
                  )}
                  {" "}Regenerate or confirm the draft still reads correctly before finalising.
                </div>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.post(`/soap-notes/notes/${note.id}/acknowledge-stale`);
                    setNote({ ...note, draft_stale: false, stale_events: [] });
                  } catch (e) {
                    toast.error("Could not clear staleness", {
                      description: e?.response?.data?.detail || e.message,
                    });
                  }
                }}
                className="text-2xs px-2 py-1 rounded border border-rose-300 bg-white text-rose-800 hover:bg-rose-100 shrink-0"
                data-testid="soap-note-acknowledge-stale-btn"
              >
                I've reviewed
              </button>
            </div>
          )}
        </DialogHeader>
        <div className="space-y-3">
          {["subjective", "objective", "assessment", "plan"].map((k) => (
            (editing || note[k]) ? (
              <section key={k}>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{k}</div>
                {editing ? (
                  <Textarea
                    rows={expanded ? 10 : (k === "subjective" || k === "plan" ? 4 : 3)}
                    value={draft[k]}
                    onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                    className="mt-1"
                    data-testid={`soap-note-edit-${k}`}
                  />
                ) : (
                  <div className="text-sm whitespace-pre-wrap" data-testid={`soap-note-view-${k}`}>{note[k]}</div>
                )}
              </section>
            ) : null
          ))}
          {editing && (
            <section>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Therapist notes</div>
              <Textarea
                rows={2}
                value={draft.therapist_notes}
                onChange={(e) => setDraft((d) => ({ ...d, therapist_notes: e.target.value }))}
                placeholder="Optional private notes — visible only inside the clinic"
                className="mt-1"
                data-testid="soap-note-edit-therapist_notes"
              />
            </section>
          )}
          {editing ? (
            <>
              <EditableExercises
                rows={draft.exercises}
                onUpdate={(i, patch) => updateRow("exercises", i, patch)}
                onAdd={() => addRow("exercises", { name: "", sets: null, reps: null, load: "", notes: "" })}
                onRemove={(i) => removeRow("exercises", i)}
              />
              <EditablePainScores
                rows={draft.pain_scores}
                onUpdate={(i, patch) => updateRow("pain_scores", i, patch)}
                onAdd={() => addRow("pain_scores", { location: "", score_0_10: null, context: "" })}
                onRemove={(i) => removeRow("pain_scores", i)}
              />
              <EditableRangeOfMotion
                rows={draft.range_of_motion}
                onUpdate={(i, patch) => updateRow("range_of_motion", i, patch)}
                onAdd={() => addRow("range_of_motion", { joint: "", movement: "", side: "", value: "" })}
                onRemove={(i) => removeRow("range_of_motion", i)}
              />
            </>
          ) : (
            <>
              {note.exercises?.length ? (
                <section>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Exercises</div>
                  <ul className="text-sm list-disc pl-5">
                    {note.exercises.map((e, i) => (
                      <li key={i}>
                        <strong>{e.name}</strong>
                        {e.sets ? ` — ${e.sets}` : ""}
                        {e.reps ? ` × ${e.reps}` : ""}
                        {e.load ? ` @ ${e.load}` : ""}
                        {e.notes ? ` (${e.notes})` : ""}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {note.pain_scores?.length ? (
                <section>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Pain scores</div>
                  <ul className="text-sm list-disc pl-5">
                    {note.pain_scores.map((p, i) => (
                      <li key={i}>{p.location}: {p.score_0_10}/10 {p.context ? `(${p.context})` : ""}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {note.range_of_motion?.length ? (
                <section>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Range of motion</div>
                  <ul className="text-sm list-disc pl-5">
                    {note.range_of_motion.map((r, i) => (
                      <li key={i}>{r.joint} {r.movement} {r.side ? `(${r.side})` : ""}: {r.value}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2 sm:space-x-0 flex-col-reverse sm:flex-row sm:flex-wrap">
          {editing ? (
            <>
              <Button
                variant="outline"
                onClick={cancelEdit}
                disabled={saving}
                className="w-full sm:w-auto h-11 sm:h-9"
                data-testid="soap-note-edit-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-emerald-700 hover:bg-emerald-800 text-white w-full sm:w-auto h-11 sm:h-9"
                data-testid="soap-note-edit-save"
              >
                {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-1" /> Save changes</>}
              </Button>
            </>
          ) : (
            <>
              {/*
                Finalise button — draft notes can be signed off in-place.
                We show it as the primary action because the user
                otherwise has no route to lock a draft (2026-02 Pamela
                Suffolk report: "there does not appear to be any way in
                which to finalise it"). Notes already in a locked
                status (finalised / amended / entered_in_error) skip
                this branch.
              */}
              {(note.status === "draft" || note.status === "ready_for_review") && (
                <Button
                  onClick={async () => {
                    if (!window.confirm(
                      "Finalise this SOAP note? Once finalised it becomes read-only; further changes require an amendment (with a reason).",
                    )) return;
                    try {
                      const r = await api.post(
                        `/soap-notes/notes/${encodeURIComponent(note.id)}/status`,
                        { status: "finalised" },
                      );
                      setNote(r.data);
                      toast.success("Note finalised", {
                        description: "Locked from further edits. Use Amend to make future changes.",
                      });
                    } catch (e) {
                      toast.error("Finalise failed", {
                        description: e?.response?.data?.detail || e.message,
                      });
                    }
                  }}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white w-full sm:w-auto h-11 sm:h-9"
                  data-testid="soap-note-finalise-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Finalise
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => window.open(
                  `${API}/soap-notes/notes/${encodeURIComponent(note.id)}/export.docx`,
                  "_blank",
                )}
                className="w-full sm:w-auto h-11 sm:h-9"
                data-testid="soap-note-download-docx"
              >
                <FileDown className="h-4 w-4 mr-1" /> DOCX
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(
                  `${API}/soap-notes/notes/${encodeURIComponent(note.id)}/export.pdf`,
                  "_blank",
                )}
                className="w-full sm:w-auto h-11 sm:h-9"
                data-testid="soap-note-download-pdf"
              >
                <FileDown className="h-4 w-4 mr-1" /> PDF
              </Button>
              <SendResourceButton
                client={{ email: note.client_email, name: note.client_name, canonicalId: note.canonical_client_id || null }}
                source={{ kind: "soap_note", id: note.id }} size="default" className="w-full sm:w-auto h-11 sm:h-9"
                testId="soap-note-send-resource-btn"
              />
              <Button
                variant="outline"
                onClick={startEdit}
                className="w-full sm:w-auto h-11 sm:h-9"
                data-testid="soap-note-edit-btn"
              >
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => setShowDelete(true)}
                  className="w-full sm:w-auto h-11 sm:h-9 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                  data-testid="soap-note-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onClose}
                className="w-full sm:w-auto h-11 sm:h-9"
              >
                <X className="h-4 w-4 mr-1" /> Close
              </Button>
            </>
          )}
        </DialogFooter>
        {showHistory && (
          <SoapNoteHistoryDialog noteId={note.id} onClose={() => setShowHistory(false)} />
        )}
        <DeleteWithReasonDialog
          open={showDelete}
          onOpenChange={setShowDelete}
          title="Delete this SOAP note?"
          description="The note will be hidden from the client's Notes tab and clinical summaries. An admin can restore it from Deleted Items."
          itemLabel={`SOAP · ${note.appointment_type || "Note"} · ${(note.created_at || "").slice(0, 10)}`}
          confirmLabel="Delete note"
          onConfirm={async (reason) => {
            await api.delete(`/soap-notes/notes/${note.id}`, {
              data: { reason },
            });
            toast.success("Note deleted");
            onClose?.();
          }}
          testidPrefix="soap-note-delete"
        />
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Editable sub-list tables (exercises / pain scores / range of motion)
// ---------------------------------------------------------------------------

function EditableListSection({ title, testid, rows, columns, onUpdate, onAdd, onRemove, addLabel }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</div>
        <Button
          size="sm" variant="outline" onClick={onAdd}
          className="h-7 px-2 text-xs"
          data-testid={`${testid}-add`}
        >
          <Plus className="h-3 w-3 mr-1" /> {addLabel}
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-2xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-2">
          None recorded yet. Click <span className="font-medium">{addLabel}</span> to add one.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div
              key={idx}
              className="grid gap-2 items-end border border-zinc-200 rounded-md p-2"
              style={{ gridTemplateColumns: `${columns.map((c) => c.width || "1fr").join(" ")} auto` }}
              data-testid={`${testid}-row-${idx}`}
            >
              {columns.map((col) => (
                <div key={col.key}>
                  <Label className="text-2xs uppercase tracking-wider text-zinc-500">{col.label}</Label>
                  {col.type === "number" ? (
                    <Input
                      type="number"
                      value={row[col.key] ?? ""}
                      onChange={(e) => onUpdate(idx, { [col.key]: e.target.value === "" ? null : parseFloat(e.target.value) })}
                      className="mt-0.5 h-8 text-sm"
                      min={col.min}
                      max={col.max}
                      step={col.step ?? "any"}
                      data-testid={`${testid}-${col.key}-${idx}`}
                    />
                  ) : col.type === "select" ? (
                    <select
                      value={row[col.key] ?? ""}
                      onChange={(e) => onUpdate(idx, { [col.key]: e.target.value })}
                      className="mt-0.5 h-8 px-2 border border-zinc-300 rounded-md text-sm bg-white w-full"
                      data-testid={`${testid}-${col.key}-${idx}`}
                    >
                      <option value="">—</option>
                      {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input
                      value={row[col.key] ?? ""}
                      onChange={(e) => onUpdate(idx, { [col.key]: e.target.value })}
                      placeholder={col.placeholder || ""}
                      className="mt-0.5 h-8 text-sm"
                      data-testid={`${testid}-${col.key}-${idx}`}
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="p-1.5 rounded-md hover:bg-red-50 text-red-600 h-8 self-end"
                title="Remove row"
                data-testid={`${testid}-remove-${idx}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function EditableExercises({ rows, onUpdate, onAdd, onRemove }) {
  return (
    <EditableListSection
      title="Exercises"
      testid="soap-note-edit-exercises"
      addLabel="Add exercise"
      rows={rows} onUpdate={onUpdate} onAdd={onAdd} onRemove={onRemove}
      columns={[
        { key: "name",  label: "Exercise", type: "text",   width: "minmax(140px, 2fr)", placeholder: "e.g. Goblet squat" },
        { key: "sets",  label: "Sets",     type: "number", width: "80px",  min: 0, step: 1 },
        { key: "reps",  label: "Reps",     type: "number", width: "80px",  min: 0, step: 1 },
        { key: "load",  label: "Load",     type: "text",   width: "100px", placeholder: "e.g. 12kg" },
        { key: "notes", label: "Notes",    type: "text",   width: "minmax(120px, 1.5fr)", placeholder: "form / cues / RPE" },
      ]}
    />
  );
}

function EditablePainScores({ rows, onUpdate, onAdd, onRemove }) {
  return (
    <EditableListSection
      title="Pain scores"
      testid="soap-note-edit-pain_scores"
      addLabel="Add pain score"
      rows={rows} onUpdate={onUpdate} onAdd={onAdd} onRemove={onRemove}
      columns={[
        { key: "location",   label: "Location", type: "text",   width: "minmax(140px, 2fr)", placeholder: "e.g. Left knee" },
        { key: "score_0_10", label: "Score /10", type: "number", width: "100px", min: 0, max: 10, step: 1 },
        { key: "context",    label: "Context",  type: "text",   width: "minmax(140px, 2fr)", placeholder: "at rest / with squat / after gardening" },
      ]}
    />
  );
}

function EditableRangeOfMotion({ rows, onUpdate, onAdd, onRemove }) {
  return (
    <EditableListSection
      title="Range of motion"
      testid="soap-note-edit-range_of_motion"
      addLabel="Add ROM"
      rows={rows} onUpdate={onUpdate} onAdd={onAdd} onRemove={onRemove}
      columns={[
        { key: "joint",    label: "Joint",    type: "text",   width: "minmax(120px, 1.5fr)", placeholder: "Knee" },
        { key: "movement", label: "Movement", type: "text",   width: "minmax(120px, 1.5fr)", placeholder: "Flexion" },
        { key: "side",     label: "Side",     type: "select", width: "110px", options: ["L", "R", "Bilateral", "N/A"] },
        { key: "value",    label: "Value",    type: "text",   width: "minmax(100px, 1.5fr)", placeholder: "110°" },
      ]}
    />
  );
}



function SoapNoteHistoryDialog({ noteId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [openVersion, setOpenVersion] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/soap-notes/notes/${noteId}/history`);
        if (!cancelled) setPayload(r.data);
      } catch (e) {
        toast.error("Could not load history", { description: e?.response?.data?.detail || e.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [noteId]);

  // Version viewer renders as a SIBLING (not a child of DialogContent) so
  // Radix portals each dialog independently and stacks them correctly.
  return (
    <>
      <Dialog open onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="soap-note-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4" /> Version history
            </DialogTitle>
            <DialogDescription className="text-xs">
              Every edit creates a snapshot of what the note looked like <em>before</em> the change.
              Click any version to view its full content.
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !payload ? null : (
            <div className="space-y-2">
              {/* Current version pinned at the top */}
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2" data-testid="soap-note-history-current">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <Badge className="bg-emerald-200 text-emerald-900 border-emerald-300 text-2xs uppercase tracking-wider border">
                    current
                  </Badge>
                  <span className="font-medium">v{payload.current.version}</span>
                  {payload.current.last_edited_at ? (
                    <span className="text-2xs text-zinc-600">
                      · {payload.current.last_edited_by_name || "Unknown"} · {new Date(payload.current.last_edited_at).toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-2xs text-zinc-500 italic">· AI (original generation)</span>
                  )}
                </div>
              </div>
              {payload.versions.length === 0 ? (
                <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-4 text-center">
                  No prior versions — this note hasn&apos;t been edited yet.
                </div>
              ) : payload.versions.map((v, idx) => {
                // Find the amendment reason (if any) that supersedes THIS version.
                const amendment = (payload.amendments || []).find((a) => a.version === v.version);
                return (
                <button
                  key={v.id}
                  onClick={() => setOpenVersion({
                    version: v,
                    // "After" content: the next-newer version if one exists,
                    // otherwise the live current note.
                    after: idx === 0
                      ? { content: payload.current.content, label: `v${payload.current.version} (current)`, when: payload.current.last_edited_at, by_name: payload.current.last_edited_by_name }
                      : { content: payload.versions[idx - 1].content, label: `v${payload.versions[idx - 1].version}`, when: payload.versions[idx - 1].snapshot_at, by_name: payload.versions[idx - 1].author_name },
                    amendment,
                  })}
                  className="w-full text-left rounded-md border border-zinc-200 px-3 py-2 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                  data-testid={`soap-note-history-version-${v.version}`}
                >
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <span className="font-medium">v{v.version}</span>
                    <span className="text-2xs text-zinc-500">
                      {v.author_name || "Unknown"} · captured {new Date(v.snapshot_at).toLocaleString()}
                    </span>
                    {amendment && (
                      <Badge className="text-2xs uppercase border bg-amber-50 text-amber-800 border-amber-300">
                        amended
                      </Badge>
                    )}
                  </div>
                  {v.next_changed_fields?.length ? (
                    <div className="mt-1 text-2xs text-zinc-600">
                      Next edit changed:{" "}
                      <span className="font-mono">{v.next_changed_fields.join(", ")}</span>
                    </div>
                  ) : null}
                  {amendment?.reason && (
                    <div className="mt-1 text-2xs text-amber-800 italic">
                      Amendment reason: &ldquo;{amendment.reason}&rdquo;
                    </div>
                  )}
                </button>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {openVersion && (
        <SoapVersionViewer entry={openVersion} onClose={() => setOpenVersion(null)} />
      )}
    </>
  );
}


// ---------------------------------------------------------------------
// Simple word-level diff — no external deps. Returns [{type, text}]
// where type ∈ "same" | "add" | "del". Used for the side-by-side
// SOAP amendment viewer.
// ---------------------------------------------------------------------
function diffWords(a, b) {
  const aTokens = (a || "").split(/(\s+)/);
  const bTokens = (b || "").split(/(\s+)/);
  // Standard LCS DP for word-level diff.
  const n = aTokens.length;
  const m = bTokens.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = aTokens[i] === bTokens[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (aTokens[i] === bTokens[j]) {
      out.push({ type: "same", text: aTokens[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: "del", text: aTokens[i] });
      i++;
    } else {
      out.push({ type: "add", text: bTokens[j] });
      j++;
    }
  }
  while (i < n) { out.push({ type: "del", text: aTokens[i++] }); }
  while (j < m) { out.push({ type: "add", text: bTokens[j++] }); }
  return out;
}


function DiffText({ before, after, side }) {
  const diff = diffWords(before, after);
  const filtered = diff.filter((d) => side === "before" ? d.type !== "add" : d.type !== "del");
  return (
    <div className="text-sm whitespace-pre-wrap leading-relaxed">
      {filtered.map((d, i) => (
        <span
          key={i}
          className={
            d.type === "del" ? "bg-red-100 text-red-900 line-through decoration-red-500"
            : d.type === "add" ? "bg-emerald-100 text-emerald-900"
            : ""
          }
        >
          {d.text}
        </span>
      ))}
    </div>
  );
}


function SoapVersionViewer({ entry, onClose }) {
  const { version, after, amendment } = entry;
  const before = version.content || {};
  const afterContent = after.content || {};
  const changed = version.next_changed_fields?.length
    ? version.next_changed_fields
    : ["subjective", "objective", "assessment", "plan", "therapist_notes"]
        .filter((k) => (before[k] || "") !== (afterContent[k] || ""));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-6xl w-[94vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="soap-note-version-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Amendment diff · v{version.version} → {after.label}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {version.author_name || "Unknown"} · captured {new Date(version.snapshot_at).toLocaleString()}
            {" → "}
            {after.by_name || "AI"} · {after.when ? new Date(after.when).toLocaleString() : "current"}
          </DialogDescription>
          {amendment?.reason && (
            <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="soap-note-diff-reason">
              <span className="font-medium">Amendment reason:</span>{" "}
              &ldquo;{amendment.reason}&rdquo;
            </div>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 space-y-4">
          {changed.length === 0 && (
            <div className="text-xs text-zinc-500 italic text-center py-6">
              No text-field differences to display.
            </div>
          )}
          {changed.map((k) => {
            // Skip non-text versioned fields — they're handled inline below.
            if (["exercises", "pain_scores", "range_of_motion", "signed_off"].includes(k)) return null;
            const bTxt = before[k] || "";
            const aTxt = afterContent[k] || "";
            if (bTxt === aTxt) return null;
            return (
              <section key={k} data-testid={`soap-note-diff-${k}`}>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="rounded border border-red-200 bg-red-50/40 p-2">
                    <div className="text-2xs uppercase tracking-wider text-red-700 mb-1">Before (v{version.version})</div>
                    <DiffText before={bTxt} after={aTxt} side="before" />
                  </div>
                  <div className="rounded border border-emerald-200 bg-emerald-50/40 p-2">
                    <div className="text-2xs uppercase tracking-wider text-emerald-700 mb-1">After ({after.label})</div>
                    <DiffText before={bTxt} after={aTxt} side="after" />
                  </div>
                </div>
              </section>
            );
          })}
          {/* Structured fields (exercises / pain / ROM) rendered as JSON diff
              is overkill — just show both sides when they differ. */}
          {["exercises", "pain_scores", "range_of_motion"].map((k) => {
            const b = JSON.stringify(before[k] || [], null, 2);
            const a = JSON.stringify(afterContent[k] || [], null, 2);
            if (b === a) return null;
            return (
              <section key={k} data-testid={`soap-note-diff-${k}`}>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">{k.replace(/_/g, " ")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <pre className="text-xs bg-red-50/40 border border-red-200 rounded p-2 overflow-x-auto">{b}</pre>
                  <pre className="text-xs bg-emerald-50/40 border border-emerald-200 rounded p-2 overflow-x-auto">{a}</pre>
                </div>
              </section>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
